import jsPDF from "jspdf";
import QRCode from "qrcode";

/**
 * Get leaf locations (locations without children)
 * A leaf location is one whose id doesn't appear as parent_id for any other location.
 */
const getLeafLocations = (locations) => {
  const parentIds = new Set(
    locations.filter(loc => loc.parent_id !== null).map(loc => loc.parent_id)
  );
  return locations.filter(loc => !parentIds.has(loc.id));
};

/**
 * Generate QR code as data URL
 */
const generateQRCode = async (data, size = 100) => {
  try {
    return await QRCode.toDataURL(data, {
      width: size,
      margin: 1,
      errorCorrectionLevel: 'M'
    });
  } catch (err) {
    console.error('QR code generation failed:', err);
    return null;
  }
};

/**
 * Export locations to PDF with QR codes
 * Each location gets a QR code on the left, with code and name displayed on the right.
 * Labels are 50mm wide x 20mm high, same width as product stickers.
 *
 * @param {Array} locations - Array of location objects with id, code, name, parent_id
 * @param {Object} options - Export options
 * @param {boolean} options.leafOnly - Export only leaf locations (default: true)
 */
export const exportLocationLabelsToPDF = async (locations, options = {}) => {
  const { leafOnly = true } = options;

  // Filter out "Onbestemd" root location
  const filteredLocations = locations.filter(loc => loc.code !== 'ONBESTEMD');

  // Get locations to export
  const locationsToExport = leafOnly
    ? getLeafLocations(filteredLocations)
    : filteredLocations;

  // Sort by code
  locationsToExport.sort((a, b) => a.code.localeCompare(b.code));

  if (locationsToExport.length === 0) {
    throw new Error('Geen locaties gevonden om te exporteren');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Label dimensions in mm (same width as product stickers, taller for more text)
  const labelWidth = 50;
  const labelHeight = 20;
  const qrSize = 18;
  const marginLeft = 8;
  const marginTop = 15;
  const marginBottom = 10;
  const gapX = 2;
  const gapY = 2;
  const cols = Math.floor((pageWidth - marginLeft * 2 + gapX) / (labelWidth + gapX));
  const rows = Math.floor((pageHeight - marginTop - marginBottom + gapY) / (labelHeight + gapY));
  const labelsPerPage = cols * rows;

  // Title
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  const title = leafOnly ? "Locatielabels" : "Locatielabels (alle locaties)";
  doc.text(title, pageWidth / 2, 8, { align: 'center' });

  // Subtitle with count
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(`${locationsToExport.length} locaties`, pageWidth / 2, 12, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // Generate QR codes first (async) - using LOC:{id} for shorter codes
  const qrCodes = await Promise.all(
    locationsToExport.map(loc => generateQRCode(`LOC:${loc.id}`, 150))
  );

  // Draw labels
  let currentPage = 0;
  let labelIndex = 0;

  for (let i = 0; i < locationsToExport.length; i++) {
    const loc = locationsToExport[i];
    const qrDataUrl = qrCodes[i];

    // Calculate position on current page
    const positionOnPage = labelIndex % labelsPerPage;
    const col = positionOnPage % cols;
    const row = Math.floor(positionOnPage / cols);

    // Check if we need a new page
    if (positionOnPage === 0 && labelIndex > 0) {
      doc.addPage();
      currentPage++;

      // Add title and subtitle to new page
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(title, pageWidth / 2, 8, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`${locationsToExport.length} locaties - pagina ${currentPage + 1}`, pageWidth / 2, 12, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }

    const x = marginLeft + col * (labelWidth + gapX);
    const y = marginTop + row * (labelHeight + gapY);

    // Light blue background to distinguish from product labels
    doc.setFillColor(232, 244, 253);
    doc.rect(x, y, labelWidth, labelHeight, 'F');

    // Draw label border (light gray, dashed for cutting guide)
    doc.setDrawColor(200, 200, 200);
    doc.setLineDashPattern([1, 1], 0);
    doc.rect(x, y, labelWidth, labelHeight);
    doc.setLineDashPattern([], 0);
    doc.setDrawColor(0, 0, 0);

    // Layout: QR code on left, text on right
    const qrX = x + 1;
    const qrY = y + (labelHeight - qrSize) / 2;
    const textAreaX = x + qrSize + 2;
    const textAreaWidth = labelWidth - qrSize - 4;

    // Draw QR code on the left
    if (qrDataUrl) {
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    }

    // Draw code (small, gray) at top right area
    doc.setFontSize(5);
    doc.setFont(undefined, "normal");
    doc.setTextColor(120, 120, 120);
    let displayCode = loc.code;
    while (doc.getTextWidth(displayCode) > textAreaWidth && displayCode.length > 3) {
      displayCode = displayCode.slice(0, -1);
    }
    if (displayCode !== loc.code) {
      displayCode += '…';
    }
    doc.text(displayCode, textAreaX, y + 3.5);

    // Draw name (larger, bold) below code
    doc.setFontSize(7);
    doc.setFont(undefined, "bold");
    doc.setTextColor(0, 0, 0);
    const locationName = loc.name || '';
    let nameEndY = y + 7; // Default position after name
    if (locationName) {
      const nameLines = doc.splitTextToSize(locationName, textAreaWidth);
      const maxNameLines = 2;
      const displayNameLines = nameLines.slice(0, maxNameLines);
      if (nameLines.length > maxNameLines) {
        const lastLine = displayNameLines[maxNameLines - 1];
        displayNameLines[maxNameLines - 1] = lastLine.length > 3
          ? lastLine.slice(0, -1) + '…'
          : lastLine;
      }
      doc.text(displayNameLines, textAreaX, y + 7);
      nameEndY = y + 7 + (displayNameLines.length - 1) * 2.5;
    }

    // Draw description below name (smaller, gray, multiple lines)
    doc.setFontSize(5);
    doc.setFont(undefined, "normal");
    doc.setTextColor(80, 80, 80);
    const description = loc.description || '';
    if (description) {
      const descStartY = nameEndY + 2.5;
      const remainingHeight = (y + labelHeight - 1) - descStartY;
      const lineHeight = 1.8;
      const maxDescLines = Math.floor(remainingHeight / lineHeight);
      if (maxDescLines > 0) {
        const descLines = doc.splitTextToSize(description, textAreaWidth);
        const displayDescLines = descLines.slice(0, maxDescLines);
        if (descLines.length > maxDescLines) {
          const lastLine = displayDescLines[maxDescLines - 1];
          displayDescLines[maxDescLines - 1] = lastLine.length > 3
            ? lastLine.slice(0, -1) + '…'
            : lastLine;
        }
        doc.text(displayDescLines, textAreaX, descStartY);
      }
    }
    doc.setTextColor(0, 0, 0);

    labelIndex++;
  }

  // Save the PDF
  const filename = leafOnly ? "locatielabels.pdf" : "locatielabels_alle.pdf";
  doc.save(filename);
};
